/**
 * Wire DTO interfaces for Ovie Health settings sections.
 *
 * IMPORTANT: Nullable fields are typed as `| null` because the upstream Ovie
 * API returns literal JSON nulls. The compose layer
 * (ai-receptionist-settings-compose.ts) strips nulls via `omitNull` when
 * building PATCH bodies. Do NOT convert `null` to `undefined` in this file.
 */

// ---------------------------------------------------------------------------
// Read-only practice context (returned by GET /settings, section=practice)
// ---------------------------------------------------------------------------

export interface IOvieSettingsPractice {
  name: string;
  timezone: string;
  multiLocation: boolean;
}

// ---------------------------------------------------------------------------
// Greeting
// ---------------------------------------------------------------------------

export interface IOvieSettingsGreetingValue {
  startMessage: string | null;
  multilocationMessage: string | null;
}

export interface IOvieSettingsGreetingPatch {
  startMessage?: string | null;
  multilocationMessage?: string | null;
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export interface IOvieSettingsMessagesValue {
  afterHoursMessage: string | null;
  publicHolidayNote: string | null;
  agentNotes: string | null;
}

export interface IOvieSettingsMessagesPatch {
  afterHoursMessage?: string | null;
  publicHolidayNote?: string | null;
  agentNotes?: string | null;
}

// ---------------------------------------------------------------------------
// Call Handling
// ---------------------------------------------------------------------------

export interface IOvieSettingsCallHandlingValue {
  transferSuspensionMessage: string | null;
  maxTransfersBeforeSuspension: number | null;
  transferSuspensionMinutes: number | null;
  repeatCallerLimit: number | null;
  repeatCallerWindowMinutes: number | null;
}

export interface IOvieSettingsCallHandlingPatch {
  transferSuspensionMessage?: string | null;
  maxTransfersBeforeSuspension?: number | null;
  transferSuspensionMinutes?: number | null;
  repeatCallerLimit?: number | null;
  repeatCallerWindowMinutes?: number | null;
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

export interface IOvieSettingsRoutingValue {
  transferPhone: string | null;
  afterhoursTransferPhone: string | null;
}

export interface IOvieSettingsRoutingPatch {
  transferPhone?: string | null;
  afterhoursTransferPhone?: string | null;
}

// ---------------------------------------------------------------------------
// Communications
// ---------------------------------------------------------------------------

export interface IOvieSettingsCommunicationsValue {
  notificationEmail: string | null;
}

export interface IOvieSettingsCommunicationsPatch {
  notificationEmail?: string | null;
}

// ---------------------------------------------------------------------------
// Booking / Catalog
// ---------------------------------------------------------------------------

export interface IOvieSettingsTreatmentAlias {
  treatmentId: string;
  name: string;
  description: string;
}

export interface IOvieSettingsBookingCatalogValue {
  treatmentAliases: IOvieSettingsTreatmentAlias[];
}

/**
 * Ovie represents aliases as an object keyed by treatmentId. A `null` value
 * deletes that treatment's alias (per-key merge); `description` is required and
 * `name` is optional.
 */
export interface IOvieTreatmentAliasWire {
  name?: string | null;
  description: string;
}

export interface IOvieSettingsBookingCatalogPatch {
  /** Full-list replace when present (Ovie cap 500). */
  bookableTreatmentIds?: string[];
  treatmentAliases?: Record<string, IOvieTreatmentAliasWire | null>;
}

// ---------------------------------------------------------------------------
// Booking / Policies
// ---------------------------------------------------------------------------

export type OvieBookingPolicyWhen = 'business_hours' | 'after_hours' | 'always';

export interface IOvieBookingPolicy {
  treatmentId: string;
  when: OvieBookingPolicyWhen;
  policy: string;
  instruction: string;
}

export interface IOvieSettingsBookingPoliciesValue {
  policies: IOvieBookingPolicy[];
}

export interface IOvieSettingsBookingPoliciesPatch {
  policies?: IOvieBookingPolicy[];
}
